import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import TrancheContractABI from "../../../abis/macrostrategytranche.abi.json";

import "./trancheinner.css";
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { formatUnits } from "viem";
import { TOKEN_SYMBOL } from "../../../utils/constants";
import PopupTrancheInfo from "./PopupTrancheInfo";
import { notifyError } from "../../../hooks/utils";

interface TrancheInnerProps {
  setActiveTranche: (tranche: number) => void;
  tranche: {
    id: number
    name: string;
    address: string;
    rewards: string;
    minContribution: number;
    status: string;
  }
}

const TrancheInner: React.FC<TrancheInnerProps> = ({ setActiveTranche, tranche }) => {
  const account = useAccount();

  const { data: maturityDate } = useReadContract({
    abi: TrancheContractABI,
    address: tranche.address as any,
    functionName: "maturityDate",
    args: [],
  });

  const payoutStatus = useMemo(() => {
    if (maturityDate) {
      const currentDate = Math.floor(Date.now() / 1000); // Current date in seconds
      const maturityDateInSeconds = Number(formatUnits(maturityDate as any ?? "0", 18)); // Convert maturity date to seconds
      return currentDate >= maturityDateInSeconds ? "Payout ready" : "Payout locked";
    }
    return "Payout LOCKED";
  }, [maturityDate]);


  const { data: totalRewards } = useReadContract({
    abi: TrancheContractABI,
    address: tranche.address as any,
    functionName: "rewards",
    args: [],
  });

  const { data: maxCap } = useReadContract({
    abi: TrancheContractABI,
    address: tranche.address as any,
    functionName: "maxCap",
    args: [],
  });

  const { data: totalDeposits } = useReadContract({
    abi: TrancheContractABI,
    address: tranche.address as any,
    functionName: "totalDeposits",
    args: [],
  });

  const trancheFull = useMemo(() => {
    if (totalDeposits && maxCap) {
      return Number(formatUnits(totalDeposits as any ?? "0", 18)) >= Number(formatUnits(maxCap as any ?? "0", 18));
    }
    return false;
  }, [totalDeposits, maxCap]);

  const stakeDisabled = useMemo(() => {
    // if trancheFull or payoutStatus === "Payout ready"
    if (trancheFull || payoutStatus === "Payout ready") {
      return true;
    }
    return false;
  }, [trancheFull, payoutStatus]);

  const percentageCompleted = useMemo(() => {
    if (totalDeposits && maxCap) {
      return Number(formatUnits(totalDeposits as any ?? "0", 18)) / Number(formatUnits(maxCap as any ?? "0", 18)) * 100;
    }
    return 0;
  }, [totalDeposits, maxCap]);

  const tokenPriceInS = 0.001; // Replace with actual token price in S ()
  const totalDaysOfTranche = 5;

  const projectedAPR = useMemo(() => {
    if (totalRewards && maxCap) {
      // return Number(formatUnits(totalRewards as any ?? "0", 18)) / Number(formatUnits(maxCap as any ?? "0", 18)) * 100;

      const maxCapInS = Number(formatUnits(maxCap as any ?? "0", 18)) * tokenPriceInS;
      const totalRewardsForTranche = Number(formatUnits(totalRewards as any ?? "0", 18)); // assuming totalRewards is in S
      const projectedAPR = (totalRewardsForTranche / maxCapInS) * (365 / totalDaysOfTranche) * 100; // Annualize the APR
      return projectedAPR.toFixed(0); // Return the projected APR as a string with 2 decimal places
    }
    return 0;
  }, [totalRewards, maxCap]);

  const [hovered, setHovered] = useState(0);

  const tranchePopupInfos = {
    1: {
      title: "Projected APR",
      description: "Annual Percentage Rate. This represents the expected return on your staked amount over a year. This vault has a 5-day lockup and actual returns are based on that duration.",
    },
    2: {
      title: "Minimum Contribution",
      description: `The minimum amount of ${TOKEN_SYMBOL} tokens you need to participate in ${tranche.name}.`,
    },
    3: {
      title: "Maturity date",
      description: `The duration you must keep your $${TOKEN_SYMBOL} locked in the tranche before you can withdraw.`,
    },
    4: {
      title: "Reward Asset",
      description: "The asset you'll receive as rewards for your participation.",
    },
    5: {
      title: "Participation Cap",
      description: "The maximum amount that can be staked in this Tranche. Once the cap is reached, no more capital will be accepted.",
    }
  }

  const [tranchePage, setTranchePage] = useState(0);


  const [claimingRewards, setClaimRewards] = useState(false);

  const { data: hash, error, isPending, writeContract } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });
  const handleClaimRewards = async () => {
    if (!account) {
      // Redirect to login or show a message
      notifyError("You need to connect wallet first.");
      return;
    }

    try {
      setClaimRewards(true);

      const transactionArgs = {
        address: tranche.address as any,
        abi: TrancheContractABI,
        functionName: 'claim',
        args: [],
      };

      writeContract(transactionArgs as any);
    } catch (error) {
      notifyError("An error occurred while trying to claim tranche rewards.");
    } finally {
      setClaimRewards(false);
    }
  };

  return (
    <div className="sc-aa05954f-3 gnIWBN">
      <div className="sc-424b74a5-0 gwBYWa">
        <h2 className="sc-424b74a5-1 jACEWN">MacroStrategy Bonds</h2>
        <div className="sc-d3e021a4-0 lsYZv">
          <h3 className="sc-d3e021a4-1 jJOvmX">{tranche.name}</h3>
          <div className="sc-a7944378-0 gTmXLT">
            <div className="sc-a7944378-1 kkKKbB">
              <div className={`sc-a7944378-2 ${tranchePage === 0 ? "edKNXf" : "dSTYar"}`} onClick={() => setTranchePage(0)}>Tranche Details</div>
              <div className={`sc-a7944378-2 ${tranchePage === 1 ? "edKNXf" : "dSTYar"}`} style={{ cursor: stakeDisabled ? "not-allowed" : "pointer" }} onClick={() => stakeDisabled ? null : setTranchePage(1)}>Your Stake</div>
            </div>
            {tranchePage === 0 && (
              <div className="sc-a7944378-3 eqHxWv">
                <div className="sc-6d564da5-0 imtxKI">
                  <div className="sc-6d564da5-1 bhqpyz">
                    <div className="sc-6d564da5-2 liCfLN">
                      <div className="sc-9393b5c4-0 gmlKlG">
                        <span className="sc-9393b5c4-1 drWCui">
                          projected APR<span className="sc-9393b5c4-2 fUEkwE" onMouseEnter={() => setHovered(1)} onMouseLeave={() => setHovered(0)}>?</span>
                        </span>
                        {hovered === 1 && (<PopupTrancheInfo popupInfo={tranchePopupInfos[1]} />)}
                      </div>
                    </div>
                    <div className="sc-6d564da5-3 gdheul">{projectedAPR}%</div>
                  </div>
                  <div className="sc-6d564da5-1 bhqpyz">
                    <div className="sc-6d564da5-2 liCfLN">
                      <div className="sc-9393b5c4-0 gmlKlG">
                        <span className="sc-9393b5c4-1 drWCui">
                          Minimum Contribution
                          <span className="sc-9393b5c4-2 fUEkwE" onMouseEnter={() => setHovered(2)} onMouseLeave={() => setHovered(0)}>?</span>
                        </span>
                        {hovered === 2 && (<PopupTrancheInfo popupInfo={tranchePopupInfos[2]} />)}
                      </div>
                    </div>
                    <div className="sc-6d564da5-3 gdheul">{tranche.minContribution} {TOKEN_SYMBOL}</div>
                  </div>
                  <div className="sc-6d564da5-1 bhqpyz">
                    <div className="sc-6d564da5-2 liCfLN">
                      <div className="sc-9393b5c4-0 gmlKlG">
                        <span className="sc-9393b5c4-1 drWCui">
                          Maturity date<span className="sc-9393b5c4-2 fUEkwE" onMouseEnter={() => setHovered(3)} onMouseLeave={() => setHovered(0)}>?</span>
                        </span>
                        {hovered === 3 && (<PopupTrancheInfo popupInfo={tranchePopupInfos[3]} />)}
                      </div>
                    </div>
                    <div className="sc-6d564da5-3 gdheul">{payoutStatus}</div>
                  </div>
                  <div className="sc-6d564da5-1 bhqpyz">
                    <div className="sc-6d564da5-2 liCfLN">
                      <div className="sc-9393b5c4-0 gmlKlG">
                        <span className="sc-9393b5c4-1 drWCui">
                          Reward Asset<span className="sc-9393b5c4-2 fUEkwE" onMouseEnter={() => setHovered(4)} onMouseLeave={() => setHovered(0)}>?</span>
                        </span>
                        {hovered === 4 && (<PopupTrancheInfo popupInfo={tranchePopupInfos[4]} />)}
                      </div>
                    </div>
                    <div className="sc-6d564da5-3 gdheul">{tranche.rewards}</div>
                  </div>
                </div>
              </div>
            )}
            {tranchePage === 1 && (
              <div className="sc-a7944378-3 eqHxWv">
              </div>
            )}

          </div>
          <div className="sc-c5a5b2e7-0 hCFaBQ">
            <div className="sc-c5a5b2e7-1 hgDdMb" style={{ textAlign: "left" }}>
              <div className="sc-9393b5c4-0 gmlKlG">
                <span className="sc-9393b5c4-1 drWCui">
                  Participation Cap<span className="sc-9393b5c4-2 fUEkwE" onMouseEnter={() => setHovered(5)} onMouseLeave={() => setHovered(0)}>?</span>
                </span>
                {hovered === 5 && (<PopupTrancheInfo popupInfo={tranchePopupInfos[5]} />)}
              </div>
            </div>
            <div className="sc-c5a5b2e7-2 eWtjhv">
              <div className="sc-c5a5b2e7-3 iBCqzX" style={{ width: percentageCompleted + "%" }} />
            </div>
            <div className="sc-c5a5b2e7-4 hYZRtl">
              <span className="sc-c5a5b2e7-5 gCDJjp">{totalDeposits ? Number(formatUnits(totalDeposits as any ?? "0", 18)).toLocaleString("en-US") : "-"} {TOKEN_SYMBOL}</span>
              <span className="sc-c5a5b2e7-5 gCDJjp">/</span>
              <span className="sc-c5a5b2e7-5 gCDJjp">{maxCap ? Number(formatUnits(maxCap as any ?? "0", 18)).toLocaleString("en-US") : "-"} {TOKEN_SYMBOL}</span>
            </div>
          </div>
          {trancheFull && (
            <div className="sc-74dbb985-0 pjook">
              <h4 className="sc-74dbb985-1 iIuZhX">{tranche.name} is complete</h4>
              <p className="sc-74dbb985-2 heTcDo">
                You can no longer invest in {tranche.name}. Tune in for future tranches!
              </p>
            </div>
          )}
          <div className="sc-43f175b-3 gdtvnF">
            {trancheFull && (
              <button className="sc-cdc39d4e-0 glfaWa"
                disabled={claimingRewards || isPending || isConfirming}
              >
                {(claimingRewards || isPending || isConfirming) ? "Claiming..." : "Claim"}
              </button>
            )}
            <button className="sc-cdc39d4e-0 gvrgXY" onClick={() => setActiveTranche(0)}>Go Back</button>
          </div>
        </div>
      </div>
    </div>

  );
}
export default TrancheInner;